import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import portfolioService from '../../services/portfolioService';
import userService from '../../services/userService';
import './PortfolioForm.css';
import { toast } from 'react-toastify';
import expenseService from '../../services/expenseService';

const PortfolioForm = ({ portfolioId, initialData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [goals, setGoals] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    owner_id: '',
    strategic_goals: [],
    status: 'Planning',
    start_date: '',
    end_date: '',
    budget_allocated: 0,
    budget_spent: 0,
    is_active: true,
    currency:'USD'
  });

  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    target_date: '',
    status: 'not_started',
    progress: 0
  });

  useEffect(() => {
    fetchUsers();

    if (initialData) {

      let parsedData = [];

      try {
        parsedData =
          typeof initialData.strategic_goals === "string"
            ? JSON.parse(initialData.strategic_goals)
            : initialData.strategic_goals;
      } catch (err) {
        console.error("Invalid JSON:", err);
      }

      setGoals(parsedData);
      console.log("parsd dataa", goals)

      setFormData({
        name: initialData.name || '',
        code: initialData.code || '',
        description: initialData.description || '',
        owner_id: initialData.owner_id || '',
        strategic_goals: parsedData || [],
        status: initialData.status || 'Planning',
        start_date: initialData.start_date || '',
        end_date: initialData.end_date || '',
        budget_allocated: initialData.budget_allocated || 0,
        budget_spent: initialData.budget_spent || 0,
        is_active: initialData.is_active !== undefined ? initialData.is_active : true
      });
    }
  }, [initialData]);

  const fetchUsers = async () => {
    try {
      const data = await userService.getAllUsers({ is_active: true });
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGoalChange = (e) => {
    const { name, value } = e.target;
    setNewGoal(prev => ({ ...prev, [name]: value }));
  };

  const addGoal = () => {
    if (!newGoal.title.trim()) {
      toast.error("Goal title is required");
      return;
    }

    const goal = {
      id: `goal-${Date.now()}`,
      title: newGoal.title,
      description: newGoal.description,
      target_date: newGoal.target_date,
      status: newGoal.status,
      progress: Number(newGoal.progress) || 0,
    };

    setFormData((prev) => ({
      ...prev,
      strategic_goals: [...(prev.strategic_goals || []), goal],
    }));

    setNewGoal({
      title: "",
      description: "",
      target_date: "",
      status: "not_started",
      progress: 0,
    });
  };

  const removeGoal = (goalId) => {
    setFormData(prev => ({
      ...prev,
      strategic_goals: prev.strategic_goals.filter(g => g.id !== goalId)
    }));
  };

  const updateGoal = (goalId, field, value) => {
    setFormData(prev => ({
      ...prev,
      strategic_goals: prev.strategic_goals?.map(g =>
        g.id === goalId ? { ...g, [field]: value } : g
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate budget
      if (parseFloat(formData.budget_spent) > parseFloat(formData.budget_allocated)) {
        setError('Budget spent cannot exceed budget allocated');
        setLoading(false);
        return;
      }

      const submitData = {
        ...formData,
        owner_id: formData.owner_id || null,
        budget_allocated: parseFloat(formData.budget_allocated) || 0,
        budget_spent: parseFloat(formData.budget_spent) || 0,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      // return false

      if (portfolioId) {
        await portfolioService.updatePortfolio(portfolioId, submitData);
      } else {
        await portfolioService.createPortfolio(submitData);
      }

      navigate('/app/portfolios');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save portfolio');
    } finally {
      setLoading(false);
    }
  };

  const budgetUtilization = portfolioService.calculateBudgetUtilization(
    formData.budget_allocated,
    formData.budget_spent
  );

  const budgetStatus = portfolioService.getBudgetStatus(budgetUtilization);

  return (
    <form onSubmit={handleSubmit} className="portfolio-form">
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="form-section">
        <h3>Basic Information</h3>

        <div className="form-row custom-row">
          <div className="form-group">
            <label htmlFor="name">
              Portfolio Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Digital Transformation, Product Development"
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">Portfolio Code</label>
            <input
              type="text"
              id="code"
              name="code"
              className="form-control"
              value={formData.code}
              onChange={handleChange}
              placeholder="e.g., DT-2024, PD-2024"
            />
            <small className="form-text">Unique identifier for the portfolio</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            className="form-control"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            placeholder="Brief description of the portfolio's purpose and scope"
          />
        </div>

        <div className="form-row custom-row">
          <div className="form-group">
            <label htmlFor="owner_id">Portfolio Owner</label>
            <select
              id="owner_id"
              name="owner_id"
              className="form-control"
              value={formData.owner_id}
              onChange={handleChange}
            >
              <option value="">No Owner Assigned</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              className="form-control"
              value={formData.status}
              onChange={handleChange}
            >
              {portfolioService.getStatusOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Timeline</h3>

        <div className="form-row custom-row">
          <div className="form-group">
            <label htmlFor="start_date">Start Date</label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              className="form-control"
              value={formData.start_date}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="end_date">End Date</label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              className="form-control"
              value={formData.end_date}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Budget Management</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="form-group">
            <label htmlFor="budget_allocated">Budget Allocated (₹)</label>
            <input
              type="number"
              id="budget_allocated"
              name="budget_allocated"
              className="form-control"
              value={formData.budget_allocated}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label htmlFor="budget_spent">Budget Spent (₹)</label>
            <input
              type="number"
              id="budget_spent"
              name="budget_spent"
              className="form-control"
              value={formData.budget_spent}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>

          <div className='form-group'>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Currency
            </label>
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {expenseService.getCurrencies().map(curr => (
                <option key={curr.value} value={curr.value}>
                  {curr.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {formData.budget_allocated > 0 && (
          <div className="budget-preview">
            <div className="budget-header">
              <span>Budget Utilization</span>
              <span className={`budget-percentage budget-${budgetStatus}`}>
                {budgetUtilization}%
              </span>
            </div>
            <div className="budget-bar">
              <div
                className={`budget-fill budget-${budgetStatus}`}
                style={{ width: `${Math.min(budgetUtilization, 100)}%` }}
              />
            </div>
            <div className="budget-summary">
              <div>
                <strong>Allocated:</strong> {portfolioService.formatBudget(formData.budget_allocated)}
              </div>
              <div>
                <strong>Spent:</strong> {portfolioService.formatBudget(formData.budget_spent)}
              </div>
              <div>
                <strong>Remaining:</strong> {portfolioService.formatBudget(
                  formData.budget_allocated - formData.budget_spent
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Strategic Goals</h3>

        <div className="goals-list">
          {Array.isArray(formData.strategic_goals)
            ? formData.strategic_goals.map(goal => (
              <div key={goal.id} className="goal-item">
                <div className="goal-header">
                  <input
                    type="text"
                    className="form-control"
                    value={goal.title}
                    onChange={(e) => updateGoal(goal.id, 'title', e.target.value)}
                    placeholder="Goal title"
                  />
                  <button
                    type="button"
                    className="btn-icon btn-danger"
                    onClick={() => removeGoal(goal.id)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
                <textarea
                  className="form-control"
                  value={goal.description}
                  onChange={(e) => updateGoal(goal.id, 'description', e.target.value)}
                  placeholder="Goal description"
                  rows="2"
                />
                <div className="goal-meta">
                  <input
                    type="date"
                    className="form-control"
                    value={goal.target_date}
                    onChange={(e) => updateGoal(goal.id, 'target_date', e.target.value)}
                  />
                  <select
                    className="form-control"
                    value={goal.status}
                    onChange={(e) => updateGoal(goal.id, 'status', e.target.value)}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on_hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <input
                    type="number"
                    className="form-control"
                    value={goal.progress}
                    onChange={(e) => updateGoal(goal.id, 'progress', e.target.value)}
                    min="0"
                    max="100"
                    placeholder="Progress %"
                  />
                </div>
              </div>
            )) : null}
        </div>

        <div className="add-goal-section">
          <h4>Add New Goal</h4>
          <input
            type="text"
            name="title"
            className="form-control"
            value={newGoal.title}
            onChange={handleGoalChange}
            placeholder="Goal title"
          />
          <textarea
            name="description"
            className="form-control"
            value={newGoal.description}
            onChange={handleGoalChange}
            placeholder="Goal description"
            rows="2"
          />
          <div className="goal-meta">
            <input
              type="date"
              name="target_date"
              className="form-control"
              value={newGoal.target_date}
              onChange={handleGoalChange}
            />
            <select
              name="status"
              className="form-control"
              value={newGoal.status}
              onChange={handleGoalChange}
            >
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <button type="button" className="btn btn-secondary" onClick={addGoal}>
            <i className="fas fa-plus"></i> Add Goal
          </button>
        </div>
      </div>

      <div className="form-section">
        <h3>Status</h3>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
            />
            <span>Active Portfolio</span>
          </label>
          <small className="form-text">
            Inactive portfolios are hidden from most views but can be reactivated
          </small>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate('/app/portfolios')}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : portfolioId ? 'Update Portfolio' : 'Create Portfolio'}
        </button>
      </div>
    </form>
  );
};

export default PortfolioForm;

